import { useEffect, useMemo, useRef, useState } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import useAuthStore from '@/store/authStore';
import useThemeStore from '@/store/themeStore';
import { clusterPOIs } from '@/utils/clusterPOIs';
import { resolveAvatarUrl } from '@/utils/avatar';
import { Coin3D } from '@/components/ui/GameIcons';

const DEFAULT_CENTER = {
  lat: Number(import.meta.env.VITE_DEFAULT_CITY_LAT || 40.6405),
  lng: Number(import.meta.env.VITE_DEFAULT_CITY_LNG || -8.6538),
};

const MAP_CAMERA = { zoom: 18 };
const PLAYER_W = 52;
const PLAYER_H = 64;
const POI_W = 72;
const POI_H = 88;

// CARTO raster basemaps: dark "night city" for dark mode, warm "voyager" for light mode.
const CARTO_ATTRIB = '&copy; OpenStreetMap contributors &copy; CARTO';
const cartoTiles = (path) => ['a', 'b', 'c'].map((s) => `https://${s}.basemaps.cartocdn.com/${path}/{z}/{x}/{y}.png`);
const STYLE_TRANSITION_MS = 500;

// Two raster layers stacked; we cross-fade their opacity on theme change for a
// smooth 500ms transition (instead of a hard setStyle reload).
const buildMapStyle = (isDark) => ({
  version: 8,
  sources: {
    cartoDark: { type: 'raster', tiles: cartoTiles('dark_all'), tileSize: 256, attribution: CARTO_ATTRIB },
    cartoLight: { type: 'raster', tiles: cartoTiles('rastertiles/voyager'), tileSize: 256, attribution: CARTO_ATTRIB },
  },
  layers: [
    { id: 'carto-light', type: 'raster', source: 'cartoLight', paint: { 'raster-opacity': isDark ? 0 : 1, 'raster-opacity-transition': { duration: STYLE_TRANSITION_MS } } },
    { id: 'carto-dark', type: 'raster', source: 'cartoDark', paint: { 'raster-opacity': isDark ? 1 : 0, 'raster-opacity-transition': { duration: STYLE_TRANSITION_MS } } },
  ],
});

const categoryMeta = {
  historia: { main: '#D4AF37', deep: '#78350F', glow: 'rgba(212,175,55,0.75)' },
  cultura: { main: '#E879F9', deep: '#701A75', glow: 'rgba(232,121,249,0.80)' },
  gastronomia: { main: '#EF4444', deep: '#7F1D1D', glow: 'rgba(239,68,68,0.80)' },
  natureza: { main: '#22C55E', deep: '#14532D', glow: 'rgba(34,197,94,0.80)' },
  comercio: { main: '#A78BFA', deep: '#4C1D95', glow: 'rgba(167,139,250,0.80)' },
  aventura: { main: '#38BDF8', deep: '#0C4A6E', glow: 'rgba(56,189,248,0.80)' },
  desporto: { main: '#FB923C', deep: '#7C2D12', glow: 'rgba(251,146,60,0.80)' },
  curiosidades: { main: '#10B981', deep: '#064E3B', glow: 'rgba(16,185,129,0.80)' },
  default: { main: '#FFD740', deep: '#7A5800', glow: 'rgba(255,215,64,0.75)' },
};

const STYLES = `
@keyframes hakken-ping {
  0% { transform: scale(1); opacity: .7; }
  80%, 100% { transform: scale(2); opacity: 0; }
}
@keyframes hakken-float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-5px); }
}
@keyframes hakken-radius-pulse {
  0% { transform: translate(-50%, -50%) scale(.08); opacity: .9; }
  70% { transform: translate(-50%, -50%) scale(1); opacity: .18; }
  100% { transform: translate(-50%, -50%) scale(1); opacity: 0; }
}
@keyframes hakken-coin-bob {
  0%, 100% { transform: translateY(0) scale(1); }
  50% { transform: translateY(-7px) scale(1.08); }
}`;

const normalizeHeading = (heading) => {
  const value = Number(heading);
  if (!Number.isFinite(value)) return null;
  return ((value % 360) + 360) % 360;
};

const getCardinalDirection = (heading) => {
  const normalized = normalizeHeading(heading);
  if (normalized == null) return '--';
  const directions = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
  return directions[Math.round(normalized / 45) % directions.length];
};

const metersToPixels = (meters, lat, zoom) => {
  const earthCircumference = 40075017;
  return meters * (256 / earthCircumference) * Math.pow(2, zoom) / Math.cos((lat * Math.PI) / 180);
};

function CategoryIcon({ category, color = '#fff' }) {
  const common = { stroke: color, fill: 'none', strokeWidth: 1.8, strokeLinecap: 'round', strokeLinejoin: 'round' };
  if (category === 'gastronomia') {
    return <svg viewBox="0 0 24 24" width="22" height="22" {...common}><path d="M8 3v6M12 3v6M8 7h4M10 9v12M17 3c2 2 2 7 0 10v8" /></svg>;
  }
  if (category === 'historia') {
    return <svg viewBox="0 0 24 24" width="22" height="22" {...common}><path d="M4 20h16M6 8h12M8 8v10M12 8v10M16 8v10M5 8l7-4 7 4" /></svg>;
  }
  if (category === 'natureza') {
    return <svg viewBox="0 0 24 24" width="22" height="22" {...common}><path d="M12 3 20 15h-5l3 6H6l3-6H4Z" fill={color} fillOpacity=".16" /><path d="M12 15v6" /></svg>;
  }
  if (category === 'comercio') {
    return <svg viewBox="0 0 24 24" width="22" height="22" {...common}><path d="M6 7h12l1 14H5Z" fill={color} fillOpacity=".16" /><path d="M9 7a3 3 0 0 1 6 0" /></svg>;
  }
  return <svg viewBox="0 0 24 24" width="22" height="22" {...common}><path d="M12 22s8-7.5 8-13A8 8 0 0 0 4 9c0 5.5 8 13 8 13Z" fill={color} fillOpacity=".2" /><circle cx="12" cy="9" r="2.5" fill={color} fillOpacity=".5" /></svg>;
}

function PlayerMarker({ avatarUrl, heading }) {
  const normalizedHeading = normalizeHeading(heading);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: PLAYER_W, height: PLAYER_H, pointerEvents: 'none' }}>
      <div style={{
        width: 46,
        height: 46,
        borderRadius: '50%',
        border: '3px solid white',
        background: 'radial-gradient(circle at 38% 35%, #5bb8f5, #1a6fad)',
        boxShadow: '0 0 0 5px rgba(58,143,209,.25), 0 0 28px 8px rgba(58,143,209,.85), 0 10px 20px rgba(0,0,0,.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
      }}>
        {avatarUrl ? (
          <img src={avatarUrl} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
        ) : (
          <svg viewBox="0 0 24 24" fill="white" width="24" height="24"><path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z" /></svg>
        )}
        <div style={{ position: 'absolute', width: 70, height: 70, borderRadius: '50%', border: '2px solid rgba(58,143,209,.5)', animation: 'hakken-ping 1.8s ease-out infinite' }} />
        {normalizedHeading != null && (
          <div style={{ position: 'absolute', top: -16, left: '50%', width: 18, height: 26, transform: `translateX(-50%) rotate(${normalizedHeading}deg)`, transformOrigin: '50% 39px' }}>
            <svg viewBox="0 0 18 26" width="18" height="26"><path d="M9 0 17 18 9 14 1 18Z" fill="#FFD740" stroke="#1a1200" strokeWidth="1.5" strokeLinejoin="round" /></svg>
          </div>
        )}
      </div>
      <svg viewBox="0 0 20 14" width="20" height="14" style={{ marginTop: -2, filter: 'drop-shadow(0 2px 4px rgba(0,0,0,.5))' }}>
        <path d="M10 14 L2 0 L10 4 L18 0 Z" fill="#1a6fad" stroke="white" strokeWidth="1.5" strokeLinejoin="round" />
      </svg>
    </div>
  );
}

function PlayerCompass({ heading, headingSource, speed, compassPermission, isUsingFallback, onRequestCompass }) {
  const normalizedHeading = normalizeHeading(heading);
  const direction = getCardinalDirection(normalizedHeading);
  const speedKmh = Number.isFinite(speed) && speed > 0 ? Math.round(speed * 3.6) : null;
  const canRequestCompass = compassPermission === 'prompt' && typeof onRequestCompass === 'function';
  const status = isUsingFallback ? 'estimado' : headingSource === 'device' ? 'telemovel' : speedKmh != null ? `${speedKmh} km/h` : 'movimento';

  return (
    <button
      type="button"
      onClick={canRequestCompass ? onRequestCompass : undefined}
      aria-label="Bussola do jogador"
      style={{
        position: 'absolute',
        top: 14,
        right: 14,
        zIndex: 40,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        border: '1px solid rgb(var(--hc-border))',
        borderRadius: 16,
        background: 'rgb(var(--hc-surface) / 0.92)',
        color: 'rgb(var(--hc-fg))',
        padding: '9px 11px',
        backdropFilter: 'blur(10px)',
        cursor: canRequestCompass ? 'pointer' : 'default',
      }}
    >
      <div style={{ position: 'relative', width: 42, height: 42, flexShrink: 0 }}>
        <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', border: '2px solid rgba(212,175,55,.65)', background: 'radial-gradient(circle, rgba(212,175,55,.18), rgba(255,255,255,.04))' }} />
        <span style={{ position: 'absolute', top: 2, left: 0, right: 0, textAlign: 'center', fontSize: 8, fontWeight: 900, color: '#FFD740' }}>N</span>
        <svg viewBox="0 0 42 42" width="42" height="42" style={{ position: 'absolute', inset: 0, transform: `rotate(${normalizedHeading ?? 0}deg)`, transition: 'transform 180ms ease-out' }}>
          <path d="M21 5 27 22 21 19 15 22Z" fill="#FFD740" stroke="#1a1200" strokeWidth="1.4" strokeLinejoin="round" />
          <path d="M21 37 15 20 21 23 27 20Z" fill="rgba(255,255,255,.35)" />
        </svg>
      </div>
      <div style={{ minWidth: 48 }}>
        <div style={{ fontSize: 16, lineHeight: '18px', fontWeight: 900, color: normalizedHeading == null ? 'rgba(255,255,255,.55)' : '#FFD740' }}>{direction}</div>
        <div style={{ marginTop: 2, fontSize: 10, lineHeight: '12px', fontWeight: 800, color: 'rgba(255,255,255,.62)' }}>{normalizedHeading == null ? 'sem rumo' : `${Math.round(normalizedHeading)} deg`}</div>
        <div style={{ marginTop: 1, fontSize: 9, lineHeight: '11px', fontWeight: 800, color: isUsingFallback ? '#fbbf24' : 'rgba(255,255,255,.45)' }}>{canRequestCompass ? 'ativar' : status}</div>
      </div>
    </button>
  );
}

function POIPin({ poi, onSelect }) {
  const collected = poi.collectedRecently;
  const inRange = poi.inRange && !collected;
  const palette = categoryMeta[poi.category] || categoryMeta.default;
  const dist = poi.distance != null ? (poi.distance < 1000 ? `${Math.round(poi.distance)} m` : `${(poi.distance / 1000).toFixed(1)} km`) : null;
  const pinFill = collected ? 'linear-gradient(160deg, #4a4a4a, #2a2a2a)' : `linear-gradient(160deg, ${palette.main}, ${palette.deep})`;
  const shadow = collected ? '0 4px 12px rgba(0,0,0,.4)' : inRange ? `0 0 0 4px ${palette.glow}, 0 0 22px 8px ${palette.glow}, 0 8px 20px rgba(0,0,0,.5)` : `0 0 0 2px ${palette.glow}, 0 0 12px 4px ${palette.glow}, 0 6px 16px rgba(0,0,0,.45)`;

  return (
    <button type="button" onClick={() => onSelect(poi)} title={poi.name} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: POI_W, height: POI_H, background: 'none', border: 0, padding: 0, cursor: 'pointer' }}>
      <div style={{ maxWidth: 70, marginBottom: 5, padding: '3px 7px', borderRadius: 999, background: 'rgb(var(--hc-surface) / 0.92)', border: '1px solid rgb(var(--hc-border))', color: 'rgb(var(--hc-fg))', fontSize: 9, fontWeight: 900, lineHeight: '12px', textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {poi.name}
        {dist && <span style={{ color: inRange ? '#4ADE80' : 'rgba(255,255,255,.5)', marginLeft: 3, fontWeight: 700 }}>{dist}</span>}
      </div>
      <div style={{ position: 'relative', flexShrink: 0 }}>
        <div style={{ width: 44, height: 44, borderRadius: '50% 50% 50% 50% / 55% 55% 45% 45%', background: pinFill, border: `2.5px solid ${inRange ? '#fff' : palette.main}`, boxShadow: shadow, opacity: collected ? .55 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
          <CategoryIcon category={poi.category} color={collected ? '#888' : '#fff'} />
          {inRange && (
            <div style={{ position: 'absolute', top: -18, right: -14, display: 'flex', alignItems: 'center', gap: 2, animation: 'hakken-coin-bob 1.4s ease-in-out infinite', filter: 'drop-shadow(0 3px 5px rgba(0,0,0,.45))' }}>
              <Coin3D style={{ width: 24, height: 24 }} />
              {poi.coinReward > 0 && (
                <span style={{ fontSize: 10, fontWeight: 900, color: '#FFD73E', textShadow: '0 1px 2px rgba(0,0,0,.8)' }}>+{poi.coinReward}</span>
              )}
            </div>
          )}
        </div>
        <div style={{ width: 0, height: 0, margin: '0 auto', borderLeft: '8px solid transparent', borderRight: '8px solid transparent', borderTop: `14px solid ${collected ? '#333' : palette.deep}`, filter: 'drop-shadow(0 3px 3px rgba(0,0,0,.4))' }} />
      </div>
    </button>
  );
}

function ClusterPin({ cluster, onSelect }) {
  const color = cluster.zone?.color || 'rgb(var(--hc-accent))';
  return (
    <button type="button" onClick={() => onSelect(cluster)} style={{ width: 48, height: 48, borderRadius: '50%', background: color, border: '3px solid white', boxShadow: `0 0 0 3px ${color}88, 0 6px 18px rgba(0,0,0,.5)`, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0, color: 'rgb(var(--hc-fg))', fontWeight: 900, fontSize: 14 }}>
      {cluster.count}
    </button>
  );
}

function useProjectedPoint(map, point) {
  const [pixel, setPixel] = useState(null);

  useEffect(() => {
    if (!map || !point?.lat || !point?.lng) return undefined;
    const update = () => {
      const projected = map.project([Number(point.lng), Number(point.lat)]);
      setPixel({ x: projected.x, y: projected.y });
    };
    update();
    map.on('move', update);
    map.on('zoom', update);
    map.on('resize', update);
    return () => {
      map.off('move', update);
      map.off('zoom', update);
      map.off('resize', update);
    };
  }, [map, point?.lat, point?.lng]);

  return pixel;
}

function MapOverlay({ map, point, children, offsetX = 0, offsetY = 0, zIndex = 20, style }) {
  const pixel = useProjectedPoint(map, point);
  if (!pixel) return null;
  return <div style={{ position: 'absolute', left: pixel.x + offsetX, top: pixel.y + offsetY, zIndex, ...style }}>{children}</div>;
}

function RadiusRing({ poi, zoom }) {
  const palette = categoryMeta[poi.category] || categoryMeta.default;
  const diameter = Math.round(metersToPixels(poi.collectionRadius || 50, Number(poi.lat), zoom) * 2);
  const color = poi.collectedRecently ? '#888888' : palette.main;
  return <div style={{ position: 'absolute', left: 0, top: 0, width: diameter, height: diameter, borderRadius: '50%', border: `2px solid ${color}`, background: `${color}22`, animation: 'hakken-radius-pulse 2.8s ease-out infinite', animationDelay: `${(poi.id % 10) * .28}s`, transform: 'translate(-50%, -50%)', pointerEvents: 'none' }} />;
}

function FreeGameMap({ pois, position, onSelect, onRecenterRef, onRequestCompass, cityViewMode, allCityPOIs }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const [map, setMap] = useState(null);
  const [zoom, setZoom] = useState(MAP_CAMERA.zoom);
  const theme = useThemeStore((s) => s.theme);
  const isDark = theme === 'dark';
  const isDarkRef = useRef(isDark);
  isDarkRef.current = isDark;
  const player = useAuthStore((s) => s.player);
  const avatarUrl = player?.avatarUrl ? resolveAvatarUrl(player.avatarUrl) : null;
  const center = position?.lat && position?.lng ? { lat: position.lat, lng: position.lng } : DEFAULT_CENTER;
  const centerRef = useRef(center);
  centerRef.current = center;

  const clustered = useMemo(() => {
    if (!cityViewMode) return null;
    return clusterPOIs(allCityPOIs || [], zoom);
  }, [cityViewMode, allCityPOIs, zoom]);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return undefined;
    const instance = new maplibregl.Map({
      container: containerRef.current,
      style: buildMapStyle(isDarkRef.current),
      center: [centerRef.current.lng, centerRef.current.lat],
      zoom: MAP_CAMERA.zoom,
      pitch: 0,
      attributionControl: false,
      maxZoom: 19,
    });
    instance.dragRotate.disable();
    instance.touchZoomRotate.disableRotation();
    instance.addControl(new maplibregl.AttributionControl({ compact: true }), 'bottom-left');
    instance.on('zoom', () => setZoom(instance.getZoom()));
    instance.on('load', () => {
      instance.resize();
      setMap(instance);
    });
    mapRef.current = instance;
    return () => {
      instance.remove();
      mapRef.current = null;
      setMap(null);
    };
  }, []);

  useEffect(() => {
    if (!onRecenterRef) return;
    onRecenterRef.current = () => {
      mapRef.current?.easeTo({ center: [centerRef.current.lng, centerRef.current.lat], zoom: MAP_CAMERA.zoom, pitch: 0, bearing: 0, duration: 450 });
    };
  }, [onRecenterRef]);

  useEffect(() => {
    mapRef.current?.resize();
  }, [pois.length, cityViewMode]);

  // Cross-fade the raster basemaps when the app theme toggles.
  useEffect(() => {
    const instance = mapRef.current;
    if (!instance || !map) return;
    const apply = () => {
      instance.setPaintProperty('carto-dark', 'raster-opacity', isDark ? 1 : 0);
      instance.setPaintProperty('carto-light', 'raster-opacity', isDark ? 0 : 1);
    };
    if (instance.isStyleLoaded()) apply();
    else instance.once('idle', apply);
  }, [isDark, map]);

  const visibleItems = cityViewMode ? (clustered || []) : pois;

  return (
    <div style={{ position: 'relative', height: '100%', width: '100%', overflow: 'hidden', background: 'rgb(var(--hc-bg))' }}>
      <style>{STYLES}</style>
      <div ref={containerRef} style={{ position: 'absolute', inset: 0 }} />
      {map && (
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
          <MapOverlay map={map} point={center} zIndex={8}>
            <div style={{ width: metersToPixels(100, center.lat, zoom) * 2, height: metersToPixels(100, center.lat, zoom) * 2, borderRadius: '50%', border: '1.5px solid rgb(var(--hc-accent) / 0.5)', background: 'rgb(var(--hc-accent) / 0.08)', transform: 'translate(-50%, -50%)' }} />
          </MapOverlay>

          {visibleItems.filter((item) => !item.isCluster).map((item) => (
            <MapOverlay key={`radius-${item.id}`} map={map} point={{ lat: Number(item.lat), lng: Number(item.lng) }} zIndex={9}>
              <RadiusRing poi={item} zoom={zoom} />
            </MapOverlay>
          ))}

          {visibleItems.map((item) => item.isCluster ? (
            <MapOverlay key={item.id} map={map} point={{ lat: Number(item.lat), lng: Number(item.lng) }} offsetX={-24} offsetY={-24} zIndex={22} style={{ pointerEvents: 'auto' }}>
              <ClusterPin
                cluster={item}
                onSelect={() => map.easeTo({ center: [Number(item.lng), Number(item.lat)], zoom: Math.min(zoom + 3, 19), duration: 450 })}
              />
            </MapOverlay>
          ) : (
            <MapOverlay key={item.id} map={map} point={{ lat: Number(item.lat), lng: Number(item.lng) }} offsetX={-(POI_W / 2)} offsetY={-POI_H} zIndex={21} style={{ pointerEvents: 'auto' }}>
              <div style={{ opacity: cityViewMode && !item.inRange ? .65 : 1, animation: item.inRange && !item.collectedRecently ? 'hakken-float 2s ease-in-out infinite' : undefined }}>
                <POIPin poi={item} onSelect={onSelect} />
              </div>
            </MapOverlay>
          ))}

          <MapOverlay map={map} point={center} offsetX={-(PLAYER_W / 2)} offsetY={-PLAYER_H} zIndex={30}>
            <PlayerMarker avatarUrl={avatarUrl} heading={position?.heading} />
          </MapOverlay>
        </div>
      )}
      <PlayerCompass
        heading={position?.heading}
        headingSource={position?.headingSource}
        speed={position?.speed}
        compassPermission={position?.compassPermission}
        isUsingFallback={position?.isUsingFallback}
        onRequestCompass={onRequestCompass}
      />
    </div>
  );
}

export default function GameMapCanvas({ pois, position, onSelect, activeCategories, onRecenterRef, onRequestCompass, cityViewMode, allCityPOIs }) {
  const filtered = activeCategories?.length ? pois.filter((p) => activeCategories.includes(p.category)) : pois;
  return (
    <FreeGameMap
      pois={filtered}
      position={position}
      onSelect={onSelect}
      onRecenterRef={onRecenterRef}
      onRequestCompass={onRequestCompass}
      cityViewMode={cityViewMode}
      allCityPOIs={allCityPOIs || []}
    />
  );
}
